import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_CASE_STUDIES = [
  {
    id: "cs-1", title: "Predictive Maintenance for National Rail Network", industry: "Railways",
    description: "AI-driven predictive analytics system reducing unplanned downtime by 73%.",
    challenge: "The national rail operator faced $2.1B in annual losses from unplanned equipment failures across 15,000 km of track.",
    solution: "Deployed edge-computing ML models on IoT sensors across the network, enabling real-time fault prediction with 96.4% accuracy.",
    results: ["73% reduction in unplanned downtime", "$840M annual savings", "96.4% prediction accuracy", "15,000 km covered"],
    image: "https://images.pexels.com/photos/10857006/pexels-photo-10857006.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tags: ["Predictive Analytics", "IoT", "Edge Computing"]
  },
  {
    id: "cs-2", title: "Satellite Imagery Analysis Platform", industry: "Space & Satellite",
    description: "Deep learning pipeline processing 4TB of satellite data daily for environmental monitoring.",
    challenge: "Manual analysis of satellite imagery took weeks and missed critical environmental changes across vast territories.",
    solution: "Built a custom CNN architecture processing multispectral satellite feeds in near real-time, detecting anomalies across 12 environmental parameters.",
    results: ["4TB daily processing", "Real-time anomaly detection", "12 environmental parameters", "98.2% detection rate"],
    image: "https://images.unsplash.com/photo-1633984726552-3ed7296dc5c9?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["Computer Vision", "Deep Learning", "Remote Sensing"]
  },
  {
    id: "cs-3", title: "Defense Intelligence Automation", industry: "Defense",
    description: "NLP-powered intelligence processing system for multi-language document analysis.",
    challenge: "Intelligence analysts were overwhelmed with unstructured data across 40+ languages, creating critical blind spots.",
    solution: "Developed a transformer-based NLP system capable of real-time translation, entity extraction, and threat-level classification across 47 languages.",
    results: ["47 languages supported", "85% analyst workload reduction", "Real-time processing", "99.1% entity accuracy"],
    image: "https://images.unsplash.com/photo-1503642153894-b3214047cd6e?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["NLP", "Transformers", "Multi-language"]
  },
  {
    id: "cs-4", title: "Smart Grid Optimization for Energy Sector", industry: "Energy",
    description: "Reinforcement learning system optimizing power distribution across a 50GW grid.",
    challenge: "Legacy grid management caused 14% energy waste and frequent brownouts during peak demand.",
    solution: "Implemented a multi-agent reinforcement learning system that dynamically balances load, predicts demand spikes, and optimizes renewable integration.",
    results: ["14% waste elimination", "$320M annual savings", "Zero brownouts in 18 months", "40% better renewable integration"],
    image: "https://images.unsplash.com/photo-1758797849151-1725021be42a?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["Reinforcement Learning", "Smart Grid", "Optimization"]
  },
  {
    id: "cs-5", title: "University Research Analytics Platform", industry: "Education",
    description: "ML platform connecting research outputs across 200+ universities for knowledge discovery.",
    challenge: "Research duplication and siloed knowledge across universities wasted millions in funding and delayed discoveries.",
    solution: "Built a graph-based ML system mapping research connections, identifying collaboration opportunities, and predicting breakthrough areas.",
    results: ["200+ universities connected", "35% reduction in research duplication", "1,200+ new collaborations", "AI-predicted 3 major breakthroughs"],
    image: "https://images.unsplash.com/photo-1725272333872-4c2d274ad493?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["Graph ML", "Knowledge Discovery", "NLP"]
  },
  {
    id: "cs-6", title: "Oil & Gas Exploration Intelligence", industry: "Oil & Gas",
    description: "Deep learning seismic analysis reducing exploration costs by 60%.",
    challenge: "Traditional seismic interpretation methods were slow, expensive, and yielded high false-positive rates for reservoir identification.",
    solution: "Created a 3D-CNN model for seismic data interpretation that identifies subsurface structures with unprecedented accuracy.",
    results: ["60% cost reduction", "3x faster interpretation", "92% reservoir identification accuracy", "$1.2B project savings"],
    image: "https://images.unsplash.com/photo-1758797849151-1725021be42a?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["3D-CNN", "Seismic Analysis", "Geophysics"]
  },
  {
    id: "cs-7", title: "Space & Drone-Based Surveillance System", industry: "Defense",
    description: "Integrated satellite and drone surveillance platform for real-time land and marine monitoring.",
    challenge: "Vast territorial borders and coastlines were impossible to monitor continuously with traditional human-operated systems, leaving critical gaps in national security.",
    solution: "Built a multi-layer AI surveillance system fusing satellite imagery with autonomous drone feeds, using real-time object detection and anomaly classification.",
    results: ["24/7 continuous monitoring", "95.7% threat detection accuracy", "80% reduction in patrol costs", "Land and marine coverage unified"],
    image: "https://images.unsplash.com/photo-1503642153894-b3214047cd6e?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["Computer Vision", "Drone AI", "Satellite Fusion", "Maritime Security"]
  },
  {
    id: "cs-8", title: "Intelligent Airport Crowd Flow Management", industry: "Airport",
    description: "AI-powered passenger tracking system dynamically allocating gates to prevent overcrowding.",
    challenge: "Static gate assignments led to severe congestion at peak hours, delayed boarding, missed connections, and poor passenger experience.",
    solution: "Deployed computer vision and flow analytics across terminal cameras to track passenger movement in real-time, dynamically reallocating gates.",
    results: ["42% reduction in gate congestion", "18% faster boarding times", "Real-time flow prediction", "30-min advance crowd alerts"],
    image: "https://images.unsplash.com/photo-1725272333872-4c2d274ad493?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["Computer Vision", "Flow Analytics", "Real-time Optimization"]
  },
  {
    id: "cs-9", title: "LLM-Powered Learning Management System", industry: "Education",
    description: "AI-driven LMS with automated exam scoring and personalized constructive feedback for students.",
    challenge: "Educators spent 60% of their time on grading and feedback, leaving little room for actual teaching.",
    solution: "Integrated a fine-tuned LLM into the university LMS to automatically score exams, generate detailed feedback, and suggest personalized learning paths.",
    results: ["85% reduction in grading time", "Instant personalized feedback", "22% improvement in student outcomes", "50,000+ students served"],
    image: "https://images.unsplash.com/photo-1725272333872-4c2d274ad493?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["LLM", "EdTech", "Automated Assessment", "Personalization"]
  },
  {
    id: "cs-10", title: "Autonomous Border Surveillance Drone Fleet", industry: "Drones & Unmanned Systems",
    description: "AI-controlled fleet of long-endurance UAVs providing 24/7 autonomous border surveillance fused with satellite intelligence.",
    challenge: "A national border agency struggled to monitor 4,800 km of remote border terrain. Manned patrols covered less than 15% of the perimeter, leaving vast blind spots.",
    solution: "Designed and manufactured a fleet of bespoke long-endurance surveillance UAVs with AI-powered target detection. The system stitches satellite imagery with live drone feeds into a unified command layer.",
    results: ["4,800 km continuous coverage", "97.3% threat detection rate", "90% reduction in manned patrols", "Satellite-drone fusion delivering 12x more intel per mission"],
    image: "https://images.unsplash.com/photo-1640952635852-1c5f243ff4bc?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["Custom UAV", "Autonomous Navigation", "Satellite Fusion", "Border Security"]
  },
  {
    id: "cs-11", title: "AI-Powered Industrial Inspection Drone & UGV System", industry: "Drones & Unmanned Systems",
    description: "Combined aerial and ground unmanned systems for autonomous infrastructure inspection in hazardous energy environments.",
    challenge: "Offshore oil platforms and high-voltage power infrastructure required dangerous manual inspections costing $48M annually, with frequent safety incidents.",
    solution: "Built bespoke inspection UAVs and UGVs equipped with thermal, LiDAR, and multispectral sensors. Drone and satellite data are stitched together to create digital twins updated in real-time.",
    results: ["75% reduction in inspection costs", "Zero safety incidents in 24 months", "3x faster fault detection", "Digital twin accuracy within 2mm"],
    image: "https://images.unsplash.com/photo-1642781859304-393590a36256?crop=entropy&cs=srgb&fm=jpg&q=85",
    tags: ["Custom UAV", "UGV", "Digital Twin", "Industrial Inspection", "Satellite Fusion"]
  }
];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

export default function CaseStudiesPage() {
  const [studies, setStudies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    axios.get(`${API}/case-studies`)
      .then(res => {
        if (Array.isArray(res.data) && res.data.length > 0) setStudies(res.data);
        else setStudies(FALLBACK_CASE_STUDIES);
      })
      .catch(() => setStudies(FALLBACK_CASE_STUDIES));
  }, []);

  const industries = ['All', ...new Set(studies.map(s => s.industry))];
  const filtered = filter === 'All' ? studies : studies.filter(s => s.industry === filter);

  return (
    <div data-testid="case-studies-page">
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-16 relative">
        <div className="absolute inset-0 bg-surface grid-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.p {...fadeIn} className="text-xs uppercase tracking-[0.3em] text-brand-accent font-bold mb-4 md:mb-6">
            Our Work
          </motion.p>
          <motion.h1
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-black tracking-tight leading-[0.9] mb-6 md:mb-8"
          >
            Case
            <br />
            <span className="text-gradient-cyan">Studies</span>
          </motion.h1>
          <motion.p
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
            className="text-lg text-slate-400 font-light max-w-xl"
          >
            Real-world AI implementations delivering measurable impact across critical industries.
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap gap-2" data-testid="case-study-filters">
            {industries.map((ind) => (
              <button
                key={ind}
                data-testid={`filter-${ind.toLowerCase().replace(/\s|&/g, '-')}`}
                onClick={() => setFilter(ind)}
                className={`text-xs uppercase tracking-[0.15em] font-bold px-5 py-2.5 border transition-colors duration-200 ${
                  filter === ind
                    ? 'border-white text-white bg-white/5'
                    : 'border-white/10 text-slate-500 hover:text-white hover:border-white/30'
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {filtered.map((study, i) => (
              <motion.div
                key={study.id}
                layout
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className={`glass-card rounded-lg overflow-hidden cursor-pointer group ${
                  i % 3 === 0 ? 'md:col-span-8' : 'md:col-span-4'
                }`}
                onClick={() => setSelected(study)}
                data-testid={`case-study-card-${study.id}`}
              >
                <div className="h-56 relative overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <p className="text-xs uppercase tracking-[0.15em] text-brand-accent font-bold mb-2">{study.industry}</p>
                    <h3 className="text-xl font-heading font-semibold">{study.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{study.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {(study.tags || []).map((tag) => (
                      <span key={tag} className="text-xs text-slate-600 font-mono">{tag}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-surface-paper border border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="case-study-modal">
          {selected && (
            <>
              <DialogHeader>
                <p className="text-xs uppercase tracking-[0.15em] text-brand-accent font-bold mb-2">{selected.industry}</p>
                <DialogTitle className="text-2xl font-heading font-bold text-white">{selected.title}</DialogTitle>
              </DialogHeader>

              <div className="mt-6 space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">The Challenge</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">{selected.challenge}</p>
                </div>

                <div>
                  <h4 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">Our Solution</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">{selected.solution}</p>
                </div>

                <div>
                  <h4 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">Results</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {(selected.results || []).map((result, i) => (
                      <div key={i} className="glass-card rounded p-4">
                        <p className="text-sm text-white font-medium">{result}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-4 border-t border-white/5">
                  {(selected.tags || []).map((tag) => (
                    <span key={tag} className="text-xs text-brand-accent border border-brand-accent/30 px-3 py-1.5 font-mono">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
